import { useLanguage } from "../i18n/LanguageContext";
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, ExternalLink, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { useState } from "react";

export const ArticleSodiumBicarbonate = () => {
  const { language } = useLanguage();
  const Arrow = language === 'he' ? ArrowLeft : ArrowRight;
  const isRTL = language === 'he';
  const [openFaq, setOpenFaq] = useState(null);

  const hebrewContent = {
    title: "סודיום ביקרבונט: המדע שמאחורי נטרול ריח גוף באופן טבעי",
    intro: "בעולם הטיפוח האישי, החיפוש אחר חלופות נקיות מאלומיניום ופרבנים מוביל חזרה לרכיב בסיסי ומוכח: סודיום ביקרבונט (סודה לשתייה). בעוד שחברות רבות מתמקדות בפיתוח מולקולות סינתטיות המוגנות בפטנטים, המדע מראה כי דווקא המנגנון הפשוט של שינוי רמת החומציות (pH) הוא הפתרון היעיל ביותר למניעת ריח מבלי לשבש את מערכות הגוף.",
    sections: [
      {
        title: "הפיזיולוגיה של הריח: מדוע אנחנו מזיעים?",
        content: "זיעה היא תהליך ביולוגי חיוני לוויסות חום הגוף ולפינוי רעלים. חשוב להבין: הזיעה עצמה היא חסרת ריח. הריח הלא נעים נוצר רק כאשר חיידקים על פני העור מפרקים את החלבונים שבזיעה.\n\nסודיום ביקרבונט פועל כסביבה בסיסית. מאחר וחיידקי הזיעה זקוקים לסביבה חומצית כדי לשגשג, העלאת ה-pH בנקודת המפגש מנטרלת את היווצרות הריח בראשיתה. בניגוד לאנטי-פרספירנטים המבוססים על אלומיניום, שיטה זו אינה חוסמת את הנקבוביות אלא מאפשרת לגוף להמשיך בתפקודו התקין."
      },
      {
        title: "איכות חומר הגלם: מעבר לסודה הביתית",
        content: "ישנה טעות נפוצה לפיה כל סוגי הסודה לשתייה זהים. לשימוש קוסמטי, קיימת חשיבות קריטית לדרגת הזיקוק ולגודל הגרגר. חומר גלם שאינו מעובד כראוי עלול לגרום לחיכוך וגירוי בעור עדין.\n\nבייצור דאודורנטים טבעיים מתקדמים, נעשה שימוש בדרגת Cosmetic Grade, חומר טהור ומעובד (כדוגמת זה המיוצר במעבדות מתמחות באיטליה ומשמש את מותג הטיפוח M'ART). רמת זיקוק זו מבטיחה שהחומר יתמזג במרקם המוצר ולא יורגש על העור, תוך שמירה על אפקטיביות מקסימלית."
      },
      {
        title: "ההיבט הפסיכולוגי של ריח הגוף",
        content: "מעבר לביולוגיה, קיים היבט אבולוציוני מרתק. המוח האנושי מתוכנת לפרש ריח גוף טבעי ומאוזן כסימן לבריאות וחיוניות. שימוש בבשמים תעשייתיים כבדים נתפס לעיתים כ\"מסכה\" מלאכותית. לעומת זאת, סביבה מנוטרלת חיידקים מאפשרת לריח הגוף הטבעי והנקי לבוא לידי ביטוי, דבר המעורר תת-מודעת תחושת ביטחון ורוגע בסביבה החברתית."
      }
    ],
    faq: [
      {
        question: "האם זה בטוח לשימוש יומיומי?",
        answer: "כן. הספרות המדעית מדגימה את היכולת האנטי-מיקרוביאלית של הרכיב ללא החדרת רעלים למחזור הדם.",
        link: "https://pubmed.ncbi.nlm.nih.gov/12017929/",
        linkText: "לעיון במחקר ב-PubMed לחצו כאן"
      },
      {
        question: "מה לגבי גירוי בעור?",
        answer: "סודה לשתייה בסיסית מאוד. כדי למנוע גירוי, מומלץ לחפש פורמולציות (כמו אלו של M'ART) המשלבות חמאות צמחיות ושמנים היוצרים שכבת הגנה ומזינים את העור תוך כדי פעולת הנטרול."
      },
      {
        question: "כמה זמן לוקח לגוף להסתגל ולעבור לריח הטבעי של הגוף?",
        answer: "המעבר מדאודורנט כימי לטבעי לוקח לרוב בין 3 ל-7 ימים, בזמן שהנקבוביות משתחררות ממשקעי אלומיניום ישנים."
      }
    ],
    academicRef: {
      title: "מקור אקדמי",
      text: "למידע אקדמי נוסף בנושא התאמת הסודה לשתייה כחומר פעיל בדאודורנט טבעי:",
      link: "https://pubmed.ncbi.nlm.nih.gov/12017929/",
      linkText: "Antimicrobial efficacy of sodium bicarbonate, PubMed"
    },
    moreInfo: "למידע נוסף על הנוסחה הפתוחה ומפרטים טכניים:"
  };

  const englishContent = {
    title: "Sodium Bicarbonate: The Science Behind Natural Body Odor Neutralization",
    intro: "In the world of personal care, the search for clean alternatives to aluminum and parabens leads back to a basic and proven ingredient: sodium bicarbonate (baking soda). While many companies focus on developing patented synthetic molecules, science shows that the simple mechanism of changing acidity levels (pH) is the most effective solution for preventing odor without disrupting body systems.",
    sections: [
      {
        title: "The Physiology of Odor: Why Do We Sweat?",
        content: "Sweating is a vital biological process for regulating body temperature and removing toxins. It's important to understand: sweat itself is odorless. The unpleasant smell is created only when bacteria on the skin break down the proteins in sweat.\n\nSodium bicarbonate acts as an alkaline environment. Since sweat bacteria need an acidic environment to thrive, raising the pH at the point of contact neutralizes odor formation at its source. Unlike aluminum-based antiperspirants, this method does not block pores but allows the body to continue functioning normally."
      },
      {
        title: "Raw Material Quality: Beyond Household Baking Soda",
        content: "There is a common misconception that all types of baking soda are identical. For cosmetic use, the degree of refinement and grain size is critically important. Improperly processed raw material can cause friction and irritation to sensitive skin.\n\nIn the production of advanced natural deodorants, Cosmetic Grade is used, a pure and processed material (such as that produced in specialized laboratories in Italy and used by the M'ART care brand). This level of refinement ensures that the material blends into the product texture and is not felt on the skin, while maintaining maximum effectiveness."
      },
      {
        title: "The Psychological Aspect of Body Odor",
        content: "Beyond biology, there is a fascinating evolutionary aspect. The human brain is programmed to interpret natural, balanced body odor as a sign of health and vitality. The use of heavy industrial perfumes is sometimes perceived as an artificial \"mask\". In contrast, a bacteria-neutralized environment allows the natural, clean body odor to be expressed, something that subconsciously evokes feelings of security and calm in social settings."
      }
    ],
    faq: [
      {
        question: "Is it safe for daily use?",
        answer: "Yes. Scientific literature demonstrates the antimicrobial ability of the ingredient without introducing toxins into the bloodstream.",
        link: "https://pubmed.ncbi.nlm.nih.gov/12017929/",
        linkText: "To review the research on PubMed, click here"
      },
      {
        question: "What about skin irritation?",
        answer: "Baking soda is very alkaline. To prevent irritation, it is recommended to look for formulations (like those from M'ART) that combine plant butters and oils that create a protective layer and nourish the skin during the neutralization process."
      },
      {
        question: "How long does it take for the body to adjust to natural body odor?",
        answer: "The transition from chemical to natural deodorant usually takes between 3 to 7 days, while the pores release old aluminum deposits."
      }
    ],
    academicRef: {
      title: "Academic Reference",
      text: "For additional academic information on the suitability of baking soda as an active ingredient in natural deodorant:",
      link: "https://pubmed.ncbi.nlm.nih.gov/12017929/",
      linkText: "Antimicrobial efficacy of sodium bicarbonate, PubMed"
    },
    moreInfo: "For more information about the open formula and technical specifications:"
  };

  const content = isRTL ? hebrewContent : englishContent;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#FAF8F5] dark:bg-[#0f1510]">
      <SEO 
        page="article-sodium-bicarbonate"
        title={`${content.title} | M'art`}
        description={content.intro.substring(0, 160)}
      />

      {/* Article Header */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link 
            to="/articles" 
            className="inline-flex items-center gap-2 text-sm text-[#4A5D43] dark:text-[#9ACD8A] hover:underline"
          >
            <ArrowRight className={`h-4 w-4 ${isRTL ? '' : 'rotate-180'}`} />
            {isRTL ? 'חזרה למאמרים' : 'Back to Articles'}
          </Link>
        </nav>

        {/* Magazine Style Header */}
        <header className="mb-10 pb-8 border-b-2 border-[#4A5D43]/20 dark:border-[#4A5D43]/30">
          <p className="text-sm font-medium text-[#4A5D43] dark:text-[#9ACD8A] uppercase tracking-wider mb-4">
            {isRTL ? 'מדע ובריאות' : 'Science & Health'}
          </p>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-medium text-[#2C3329] dark:text-white leading-tight mb-6">
            {content.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-[#6B7266] dark:text-[#9A9790]">
            <span>{isRTL ? '4 דקות קריאה' : '4 min read'}</span>
            <span>•</span>
            <span>{isRTL ? "M'ART" : "M'ART"}</span>
          </div>
        </header>

        {/* Lead Paragraph - Magazine Style */}
        <div className="mb-10">
          <p className="text-lg sm:text-xl leading-relaxed text-[#2C3329] dark:text-[#E8E4DC] font-serif first-letter:text-5xl first-letter:font-bold first-letter:float-right first-letter:mr-0 first-letter:ml-3 first-letter:text-[#4A5D43] dark:first-letter:text-[#9ACD8A]" style={{ firstLetter: isRTL ? { float: 'right', marginLeft: '0.75rem', marginRight: 0 } : {} }}>
            {content.intro}
          </p>
        </div>

        {/* Article Sections */}
        <div className="space-y-10">
          {content.sections.map((section, index) => (
            <section key={index} className="article-section">
              <h2 className="font-serif text-xl sm:text-2xl font-medium text-[#2C3329] dark:text-white mb-4 pb-2 border-b border-[#E8E4DC] dark:border-[#2a3a2a]">
                {section.title}
              </h2>
              {section.content.split('\n\n').map((paragraph, pIndex) => (
                <p 
                  key={pIndex} 
                  className="text-[#4a4a4a] dark:text-[#c5c5c5] leading-[1.9] mb-4 text-base"
                >
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </div>

        {/* Decorative Divider */}
        <div className="my-12 flex items-center justify-center">
          <div className="h-px w-16 bg-[#4A5D43]/30"></div>
          <div className="mx-4 w-2 h-2 rounded-full bg-[#4A5D43]"></div>
          <div className="h-px w-16 bg-[#4A5D43]/30"></div>
        </div>

        {/* FAQ Section - Beautiful Design */}
        <section className="mt-12">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#2C3329] dark:text-white text-center mb-8">
            {isRTL ? 'שאלות ותשובות' : 'FAQ'}
          </h2>
          
          <div className="space-y-4">
            {content.faq.map((item, index) => (
              <div 
                key={index}
                className={`bg-white dark:bg-[#1a231a] rounded-2xl border border-[#E8E4DC] dark:border-[#2a3a2a] overflow-hidden transition-all duration-300 ${openFaq === index ? 'shadow-lg ring-2 ring-[#4A5D43]/20' : 'shadow-sm hover:shadow-md'}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-right"
                >
                  <h3 className="font-medium text-[#2C3329] dark:text-white text-base sm:text-lg flex-1">
                    {item.question}
                  </h3>
                  <div className={`${isRTL ? 'mr-4' : 'ml-4'} w-10 h-10 rounded-full bg-[#4A5D43]/10 dark:bg-[#4A5D43]/20 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                    <ChevronDown className="h-5 w-5 text-[#4A5D43] dark:text-[#9ACD8A]" />
                  </div>
                </button>
                
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-6 pb-6 pt-0">
                    <div className="pt-4 border-t border-[#E8E4DC] dark:border-[#2a3a2a]">
                      <p className="text-[#6B7266] dark:text-[#9A9790] leading-relaxed">
                        {item.answer}
                      </p>
                      {item.link && (
                        <a 
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-3 text-[#4A5D43] dark:text-[#9ACD8A] hover:underline text-sm font-medium"
                        >
                          {item.linkText}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Academic Reference Box */}
        <section className="mt-8 rounded-2xl overflow-hidden border-2 border-[#4A5D43]/20 bg-gradient-to-br from-[#F0F5EF] to-[#FAF8F5] dark:from-[#1a231a] dark:to-[#0f1510]">
          <div className="h-1 bg-gradient-to-r from-[#4A5D43] to-[#6B8E6B]" />
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#4A5D43]/10 rounded-full flex items-center justify-center shrink-0">
                <BookOpen className="h-5 w-5 text-[#4A5D43]" />
              </div>
              <h3 className="font-serif text-lg text-[#2C3329] dark:text-white font-medium">
                {content.academicRef.title}
              </h3>
            </div>
            <p className="text-[#6B7266] dark:text-[#9A9790] mb-4 text-sm leading-relaxed">
              {content.academicRef.text}
            </p>
            <a
              href={content.academicRef.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="academic-ref-link"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white dark:bg-[#1a231a] border border-[#4A5D43]/20 rounded-xl text-[#4A5D43] dark:text-[#9ACD8A] text-sm font-medium hover:bg-[#4A5D43]/5 hover:border-[#4A5D43]/40 transition-all shadow-sm"
            >
              <ExternalLink className="h-4 w-4" />
              {content.academicRef.linkText}
            </a>
          </div>
        </section>

        {/* More Info Link */}
        <section className="mt-12 text-center py-8 px-6 bg-gradient-to-b from-[#F5F2EB] to-[#FAF8F5] dark:from-[#1a231a] dark:to-[#0f1510] rounded-2xl border border-[#E8E4DC] dark:border-[#2a3a2a]">
          <p className="text-[#6B7266] dark:text-[#9A9790] mb-4">
            {content.moreInfo}
          </p>
          <a 
            href="https://www.deo.co.il"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#4A5D43] dark:text-[#9ACD8A] font-medium hover:underline"
          >
            www.deo.co.il
            <ExternalLink className="h-4 w-4" />
          </a>
        </section>

        {/* Back to Articles */}
        <div className="mt-10 text-center">
          <Link 
            to="/articles"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A5D43] hover:bg-[#3A4A35] text-white rounded-full font-medium transition-colors"
          >
            <ArrowRight className={`h-4 w-4 ${isRTL ? '' : 'rotate-180'}`} />
            {isRTL ? 'חזרה לכל המאמרים' : 'Back to All Articles'}
          </Link>
        </div>
      </article>

      {/* SEO Hidden Content */}
      <div className="sr-only" aria-hidden="true">
        <h1>סודיום ביקרבונט: המדע שמאחורי נטרול ריח גוף באופן טבעי</h1>
        <p>בעולם הטיפוח האישי, החיפוש אחר חלופות נקיות מאלומיניום ופרבנים מוביל חזרה לרכיב בסיסי ומוכח: סודיום ביקרבונט (סודה לשתייה). בעוד שחברות רבות מתמקדות בפיתוח מולקולות סינתטיות המוגנות בפטנטים, המדע מראה כי דווקא המנגנון הפשוט של שינוי רמת החומציות (pH) הוא הפתרון היעיל ביותר למניעת ריח מבלי לשבש את מערכות הגוף.</p>
        <h2>הפיזיולוגיה של הריח: מדוע אנחנו מזיעים?</h2>
        <p>זיעה היא תהליך ביולוגי חיוני לוויסות חום הגוף ולפינוי רעלים. חשוב להבין: הזיעה עצמה היא חסרת ריח. הריח הלא נעים נוצר רק כאשר חיידקים על פני העור מפרקים את החלבונים שבזיעה.</p>
        <p>סודיום ביקרבונט פועל כסביבה בסיסית. מאחר וחיידקי הזיעה זקוקים לסביבה חומצית כדי לשגשג, העלאת ה-pH בנקודת המפגש מנטרלת את היווצרות הריח בראשיתה. בניגוד לאנטי-פרספירנטים המבוססים על אלומיניום, שיטה זו אינה חוסמת את הנקבוביות אלא מאפשרת לגוף להמשיך בתפקודו התקין.</p>
        <h2>איכות חומר הגלם: מעבר לסודה הביתית</h2>
        <p>ישנה טעות נפוצה לפיה כל סוגי הסודה לשתייה זהים. לשימוש קוסמטי, קיימת חשיבות קריטית לדרגת הזיקוק ולגודל הגרגר. חומר גלם שאינו מעובד כראוי עלול לגרום לחיכוך וגירוי בעור עדין.</p>
        <p>בייצור דאודורנטים טבעיים מתקדמים, נעשה שימוש בדרגת Cosmetic Grade, חומר טהור ומעובד (כדוגמת זה המיוצר במעבדות מתמחות באיטליה ומשמש את מותג הטיפוח M'ART). רמת זיקוק זו מבטיחה שהחומר יתמזג במרקם המוצר ולא יורגש על העור, תוך שמירה על אפקטיביות מקסימלית.</p>
        <h2>ההיבט הפסיכולוגי של ריח הגוף</h2>
        <p>מעבר לביולוגיה, קיים היבט אבולוציוני מרתק. המוח האנושי מתוכנת לפרש ריח גוף טבעי ומאוזן כסימן לבריאות וחיוניות. שימוש בבשמים תעשייתיים כבדים נתפס לעיתים כ"מסכה" מלאכותית. לעומת זאת, סביבה מנוטרלת חיידקים מאפשרת לריח הגוף הטבעי והנקי לבוא לידי ביטוי, דבר המעורר תת-מודעת תחושת ביטחון ורוגע בסביבה החברתית.</p>
        <h2>שאלות ותשובות</h2>
        <h3>האם זה בטוח לשימוש יומיומי?</h3>
        <p>כן. הספרות המדעית מדגימה את היכולת האנטי-מיקרוביאלית של הרכיב ללא החדרת רעלים למחזור הדם.</p>
        <h3>מה לגבי גירוי בעור?</h3>
        <p>סודה לשתייה בסיסית מאוד. כדי למנוע גירוי, מומלץ לחפש פורמולציות (כמו אלו של M'ART) המשלבות חמאות צמחיות ושמנים היוצרים שכבת הגנה ומזינים את העור תוך כדי פעולת הנטרול.</p>
        <h3>כמה זמן לוקח לגוף להסתגל ולעבור לריח הטבעי של הגוף?</h3>
        <p>המעבר מדאודורנט כימי לטבעי לוקח לרוב בין 3 ל-7 ימים, בזמן שהנקבוביות משתחררות ממשקעי אלומיניום ישנים.</p>
      </div>
    </div>
  );
};

export default ArticleSodiumBicarbonate;
